import React, { useEffect, useState, useMemo } from "react";
import api from "../../../../api/axios";
import { useCurrentTenant } from "../../../../hooks/useCurrentTenant"
import "./DevicePickerModal.css";

const DevicePickerModal = ({
  isOpen,
  onClose,
  onAddDevices,
  existingDevices = [],
}) => {
  const currentTenantId = useCurrentTenant();
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!isOpen) return;

    const load = async () => {
      try {
        const { data } = await api.get("/subgroup");

        const tenantSubGroups = (Array.isArray(data) ? data : []).filter(
          (s) =>
            s.tenantId === currentTenantId &&
            s.ownership === "Tenant"
        );

        const initRows = tenantSubGroups.map((sg) => ({
          selected: false,
          id: sg.id,
          name: sg.name ?? "",
          articleNumber: sg.articleNumber ?? "",
          manufacturer: sg.manufacturer ?? "",
          quantity: 0,
          placeholderQuantity: 0,
          subGroup: sg,
        }));

        setRows(initRows);
      } catch (err) {
        console.error("Failed to load subgroups:", err);
      }
    };

    load();
  }, [isOpen, currentTenantId]);

  const updateRowNumber = (id, field, value) => {
    const raw = Number(value);
    const n = Number.isFinite(raw) && raw >= 0 ? raw : 0;

    setRows((prev) =>
      prev.map((row) => {
        if (row.id !== id) return row;

        const next = { ...row, [field]: n };

        // Compute updated values
        const qty = field === "quantity" ? n : row.quantity;
        const placeholder =
          field === "placeholderQuantity" ? n : row.placeholderQuantity;

        // Auto-select/deselect logic restored:
        // - If either is > 0 → selected
        // - If both 0 → deselected
        if (qty > 0 || placeholder > 0) {
          next.selected = true;
        } else {
          next.selected = false;
        }

        return next;
      }),
    );
  };

  const toggleSelect = (id, checked) => {
    setRows((prev) =>
      prev.map((r) => (r.id === id ? { ...r, selected: checked } : r)),
    );
  };

  const handleDeselectAll = () => {
    setRows((prev) => prev.map((r) => ({ ...r, selected: false })));
  };

  const handleAdd = () => {
    const selected = rows.filter(
      (r) => r.selected && (r.quantity > 0 || r.placeholderQuantity > 0),
    );
    onAddDevices(selected);
    onClose();
  };

  if (!isOpen) return null;

  const filteredRows = rows.filter((r) => {
    const q = search.toLowerCase();
    return (
      r.name.toLowerCase().includes(q) ||
      r.articleNumber.toLowerCase().includes(q) ||
      r.manufacturer.toLowerCase().includes(q)
    );
  });

  return (
    <div className="device-picker-backdrop">
      <div className="device-picker-modal">
        <h2>Select Devices</h2>

        {/* NEW TOP BAR */}
        <div className="device-top-bar">
          <button className="btn-deselect-all" onClick={handleDeselectAll}>
            Deselect All
          </button>

          <input
            type="text"
            className="input device-search"
            placeholder="Search devices..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="device-table-wrapper">
          <table className="device-picker-table">
            <thead>
              <tr>
                <th>Select</th>
                <th>Type</th>
                <th>Name</th>
                <th>Manufacturer</th>
                <th>Part Number</th>
                <th>Qty</th>
                <th>Placeholder</th>
              </tr>
            </thead>

            <tbody>
              {filteredRows.map((row) => (
                <tr key={row.id}>
                  <td>
                    <input
                      type="checkbox"
                      checked={row.selected}
                      onChange={(e) => toggleSelect(row.id, e.target.checked)}
                    />
                  </td>

                  <td>
                    <span className="locked-cell">{row.subGroup.type}</span>
                  </td>

                  <td>
                    <span className="locked-cell">{row.name}</span>
                  </td>
                  <td>
                    <span className="locked-cell">{row.manufacturer}</span>
                  </td>

                  <td>
                    <span className="locked-cell">{row.articleNumber}</span>
                  </td>

                  <td>
                    <input
                      type="number"
                      className="input number"
                      min={0}
                      step={1}
                      value={row.quantity}
                      onChange={(e) =>
                        updateRowNumber(row.id, "quantity", e.target.value)
                      }
                    />
                  </td>

                  <td>
                    <input
                      type="number"
                      className="input number"
                      min={0}
                      step={1}
                      value={row.placeholderQuantity}
                      onChange={(e) =>
                        updateRowNumber(
                          row.id,
                          "placeholderQuantity",
                          e.target.value,
                        )
                      }
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="device-picker-actions">
          <button className="btn-primary-devpick" onClick={handleAdd}>
            Add Selected
          </button>
          <button className="btn-secondary-devpick" onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default DevicePickerModal;
